/**
 * AuthProvider + ProtectedRoute components. All session state, effects and
 * network calls live in useAuthSession / authApi / authBootstrap so this file
 * only holds components (react-refresh-clean).
 */
import React, { ReactNode } from 'react';
import { AuthContext, useAuth } from './AuthContext';
import type { User } from './AuthContext';
import { useAuthSession } from './useAuthSession';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const contextValue = useAuthSession();

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// HOC for protected routes
interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  requiredUserType?: User['user_type'];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredUserType,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>; // You can replace with a proper loading component
  }

  if (requireAuth && !isAuthenticated) {
    return <div>Please log in to access this page.</div>; // You can replace with login redirect
  }

  if (requiredUserType && user?.user_type !== requiredUserType) {
    return <div>Access denied. Insufficient permissions.</div>;
  }

  return children;
};
